import { SYSTEMS } from '../../constants/systems';
import { useTemperature } from '../../hooks/useTemperature';
import ControlBox from '../controlBox/ControlBox';
import {
  ConsumptionImgWrapper,
  ConsumptionTitleG,
  ConsumptionTitleS,
  ConsumptionTitleWrapper,
  ConsumptionUnit,
  ConsumptionWrapper,
  Img,
  SectionConsumption,
  SectionMain,
  SectionMainContentClose,
  SectionSub,
  ShutOffButton,
} from './bodyStyles';
import {
  HeaderHat,
  HeaderTitle,
  SectionHeader,
  SectionMainContentExpand,
  Wrapper,
} from './frameStyles';
import { programIcon } from './machineCardFacts';
import {
  ContentSpanWrapper,
  DisplayInfo,
  DisplayTempMobile,
  DisplayWrapper,
  LeftWrapper,
  MainTitle,
  MainTitleWrapper,
  MainWrapperTop,
  MobileButton,
  MobileButtonHole,
  MobileButtonTop,
  MobileImg,
  RightTop,
  RightWrapper,
  SectionMobileInfoDisplay,
  TitleAndConsumptionWrapper,
} from './mobileStyles';
import { InfoTitle } from './statusStyles';

const battery = (isOff, on, image) => (isOff || !on ? '/images/battery-none.svg' : image);

/**
 * The mobile card: title, consumption, the running/ready program's icon,
 * badges, and one button that opens the device's ControlBox below.
 * `card` is useMachineCard's result plus system, location, machine,
 * title, consumption, temps, selectedProgramSrc and indivLocationName.
 */
const MobileMachineCard = (card) => {
  const { system, location, machine, device, title, consumption, temps, readOnly } = card;
  const { programs, sibling } = SYSTEMS[system];
  const { isOff, isFaults, isWifi, isGp, isEbp, openMachineController } = device;
  const { isF, unit } = useTemperature();
  const icon = programIcon(device, programs);
  const iconSrc = icon.src ?? card.selectedProgramSrc;

  return (
    <Wrapper isExpanded={openMachineController} isMobile isFaults={isFaults}>
      <SectionHeader isMobile hasSibling={Boolean(sibling)}>
        {sibling && (
          <HeaderHat
            first
            isMobile
            imgSrc={
              isOff || !device[sibling.activeField]
                ? '/images/MC-machine-header2-mobile.svg'
                : '/images/MC-machine-header1-mobile.svg'
            }
          >
            <HeaderTitle isMobile>{sibling.label}</HeaderTitle>
          </HeaderHat>
        )}
        <HeaderHat isMobile imgSrc='/images/MC-machine-header2-mobile.svg'>
          <HeaderTitle isMobile>ate</HeaderTitle>
        </HeaderHat>
      </SectionHeader>

      <SectionMainContentClose
        isFaults={isFaults}
        isOff={isOff}
        isMobile
        isExpanded={openMachineController}
      >
        <SectionMain isOff={isOff} isMobile>
          <MainWrapperTop isOff={isOff}>
            <LeftWrapper>
              <ShutOffButton onClick={card.toggleShutOff} isOff={isOff} isLocked={readOnly} isMobile>
                <Img isShutOff src={isOff ? '/images/turn-on.svg' : '/images/shut-off.svg'} />
              </ShutOffButton>

              <TitleAndConsumptionWrapper>
                <MainTitleWrapper>
                  <MainTitle>{title}</MainTitle>
                </MainTitleWrapper>

                <SectionConsumption isOff={isOff} isMobile>
                  <ConsumptionWrapper isOff={isOff} isMobile>
                    <ConsumptionImgWrapper isMobile>
                      <Img
                        energy
                        src={
                          isOff
                            ? '/images/MC-energy-consumption-disabled.svg'
                            : '/images/MC-energy-consumption.svg'
                        }
                      />
                    </ConsumptionImgWrapper>
                    <ContentSpanWrapper>
                      <ConsumptionTitleS isOff={isOff} first isMobile>
                        {consumption.source} consumption
                      </ConsumptionTitleS>
                      <ConsumptionTitleWrapper isMobile>
                        <ConsumptionTitleG isOff={isOff} isMobile>
                          {consumption.value}
                        </ConsumptionTitleG>
                        <ConsumptionUnit isOff={isOff} isMobile>
                          {consumption.unit}
                        </ConsumptionUnit>
                      </ConsumptionTitleWrapper>
                    </ContentSpanWrapper>
                  </ConsumptionWrapper>
                </SectionConsumption>
              </TitleAndConsumptionWrapper>
            </LeftWrapper>

            <RightWrapper isOff={isOff}>
              <RightTop isOff={isOff}>
                <MobileButton
                  isOff={isOff}
                  noButton
                  isActivated={icon.isActivated}
                  isReady={icon.isReady}
                >
                  <MobileButtonHole isOff={isOff} isActivated={icon.isActivated}>
                    {iconSrc && <Img src={iconSrc} />}
                  </MobileButtonHole>
                </MobileButton>

                <SectionMobileInfoDisplay>
                  <DisplayWrapper>
                    <DisplayInfo isLeft isOff={isOff}>
                      <MobileImg
                        src={
                          isOff
                            ? '/images/fault-sm-disabled.svg'
                            : isFaults
                              ? '/images/fault-sm-active.svg'
                              : '/images/fault-sm.svg'
                        }
                      />
                    </DisplayInfo>
                    <DisplayInfo isOff={isOff}>
                      <MobileImg
                        src={isOff || !isWifi ? '/images/wifi-none.svg' : '/images/wifi-active.svg'}
                      />
                    </DisplayInfo>
                  </DisplayWrapper>

                  <DisplayWrapper>
                    <DisplayInfo isSpaceBetween isLeft isOff={isOff}>
                      <InfoTitle state={isGp} title='gp' isOff={isOff} isMobile>
                        gp
                      </InfoTitle>
                      <MobileImg src={battery(isOff, isGp, '/images/gp-battery.svg')} />
                    </DisplayInfo>
                    <DisplayInfo isSpaceBetween isOff={isOff}>
                      <InfoTitle title='ebp' state={isEbp} isMobile>
                        ebp
                      </InfoTitle>
                      <MobileImg src={battery(isOff, isEbp, '/images/ebp-battery.svg')} />
                    </DisplayInfo>
                  </DisplayWrapper>
                </SectionMobileInfoDisplay>

                <MobileButton
                  isOff={isOff}
                  isExpanded={openMachineController}
                  onClick={card.toggleMobileControls}
                >
                  <MobileButtonHole isExpanded={openMachineController} isOff={isOff}>
                    <MobileButtonTop isExpanded={openMachineController} isOff={isOff}>
                      <Img
                        expandIcon
                        src={
                          isOff
                            ? '/images/MC-expand-disabled.svg'
                            : openMachineController
                              ? '/images/MC-expand-true.svg'
                              : '/images/MC-expand-false.svg'
                        }
                      />
                    </MobileButtonTop>
                  </MobileButtonHole>
                </MobileButton>
              </RightTop>
            </RightWrapper>
          </MainWrapperTop>
        </SectionMain>

        {openMachineController ? (
          <SectionMainContentExpand isMobile>
            <ControlBox
              isMobile
              swtName={system}
              location={location}
              machine={machine}
              setTemp={temps.setTemp}
              indivLocationName={card.indivLocationName}
            />
          </SectionMainContentExpand>
        ) : (
          <SectionSub isOff={isOff} isMobile>
            <DisplayTempMobile isOff={isOff}>
              set t. {temps.setTemp ? temps.setTemp : '---'}
              {isF ? '°F' : '°C'}
            </DisplayTempMobile>
            <DisplayTempMobile isOff={isOff}>
              cur. t. {isOff ? '---' : `${temps.currentTemp}`}
              {unit}
            </DisplayTempMobile>
          </SectionSub>
        )}
      </SectionMainContentClose>
    </Wrapper>
  );
};

export default MobileMachineCard;
